import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  ListTree,
  BookOpen,
  BookText,
  Scale,
  TrendingUp,
  Building2,
  Users,
  LogOut,
  ChevronDown,
  Check,
  Columns3,
  Receipt,
  ShoppingCart,
  UserRound,
  Truck,
  FileSignature,
  FilePlus2,
  Inbox,
  Wallet,
  Menu,
  Landmark,
  FileSpreadsheet,
  GitCompareArrows,
  Send,
  PiggyBank,
  Banknote,
  CalendarDays,
  CalendarClock,
  ArrowLeftRight,
  FileBarChart,
  HardDrive,
  Boxes,
  ClipboardList,
  Coins,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { usePermissions } from "../hooks/usePermissions";
import DemoBanner from "./DemoBanner";
import SubscriptionBanner, { SubscriptionNotice } from "./SubscriptionBanner";
import ThemeToggle from "./ThemeToggle";
import OrganizadorReminders from "./OrganizadorReminders";
import BrandLogo from "./BrandLogo";
import { api, extractError } from "../lib/api";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "./ui/sheet";

const NAV = [
  { to: "/app/dashboard", label: "Resumen", icon: LayoutDashboard, tid: "nav-dashboard", module: "dashboard" },
  { to: "/app/organizador", label: "Calendario", icon: CalendarDays, tid: "nav-organizador", module: "organizador" },
  { to: "/app/plan-cuentas", label: "Plan de cuentas", icon: ListTree, tid: "nav-plan-cuentas", module: "plan_cuentas" },
  { to: "/app/centros-costo", label: "Centros de costo", icon: Columns3, tid: "nav-centros-costo", module: "plan_cuentas" },
  { to: "/app/libro-diario", label: "Libro diario", icon: BookOpen, tid: "nav-libro-diario", module: "libro_diario" },
  { to: "/app/activo-fijo", label: "Activo fijo", icon: HardDrive, tid: "nav-activo-fijo", module: "libro_diario" },
  { to: "/app/existencias", label: "Existencias", icon: Boxes, tid: "nav-existencias", module: "libro_diario" },
  { to: "/app/provisiones", label: "Provisiones", icon: ClipboardList, tid: "nav-provisiones", module: "libro_diario" },
  { to: "/app/multimoneda", label: "Multimoneda", icon: Coins, tid: "nav-multimoneda", module: "libro_diario" },
  { to: "/app/libro-mayor", label: "Libro mayor", icon: BookText, tid: "nav-libro-mayor", module: "libro_mayor" },
];

const REPORTES_NAV = [
  { to: "/app/balance-general", label: "Balance general", icon: Scale, tid: "nav-balance-general", module: "reportes" },
  { to: "/app/balance-8-columnas", label: "Balance 8 columnas", icon: Columns3, tid: "nav-balance-8", module: "reportes" },
  { to: "/app/estado-resultados", label: "Estado de resultados", icon: TrendingUp, tid: "nav-estado-resultados", module: "reportes" },
  { to: "/app/flujo-caja", label: "Flujo de caja", icon: Wallet, tid: "nav-flujo-caja", module: "reportes" },
  { to: "/app/informe-tributario", label: "Informe tributario", icon: FileBarChart, tid: "nav-informe-tributario", module: "informe_tributario" },
  { to: "/app/declaraciones", label: "Declaraciones", icon: FileSpreadsheet, tid: "nav-declaraciones", module: "informe_tributario" },
];

const COMERCIAL_NAV = [
  { to: "/app/facturador", label: "Facturador", icon: FilePlus2, tid: "nav-facturador", module: "facturador" },
  { to: "/app/documentos-recibidos", label: "Documentos recibidos", icon: Inbox, tid: "nav-documentos-recibidos", module: "facturador" },
  { to: "/app/clientes", label: "Clientes", icon: UserRound, tid: "nav-clientes", module: "comercial" },
  { to: "/app/proveedores", label: "Proveedores", icon: Truck, tid: "nav-proveedores", module: "comercial" },
  { to: "/app/libro-ventas", label: "Libro de ventas", icon: Receipt, tid: "nav-libro-ventas", module: "comercial" },
  { to: "/app/libro-compras", label: "Libro de compras", icon: ShoppingCart, tid: "nav-libro-compras", module: "comercial" },
  { to: "/app/boletas-honorarios", label: "Libro honorarios", icon: FileSignature, tid: "nav-boletas-honorarios", module: "comercial" },
  { to: "/app/cuenta-corriente", label: "Cuenta corriente", icon: ArrowLeftRight, tid: "nav-cuenta-corriente", module: "comercial" },
];

const REMUNERACIONES_NAV = [
  { to: "/app/remuneraciones", label: "Remuneraciones", icon: Banknote, tid: "nav-remuneraciones", module: "remuneraciones" },
];

const TESORERIA_NAV = [
  { to: "/app/cuentas-bancarias", label: "Cuentas bancarias", icon: Landmark, tid: "nav-cuentas-bancarias", module: "tesoreria" },
  { to: "/app/cartola", label: "Cartola bancaria", icon: FileSpreadsheet, tid: "nav-cartola", module: "tesoreria" },
  { to: "/app/conciliacion", label: "Conciliación", icon: GitCompareArrows, tid: "nav-conciliacion", module: "tesoreria" },
  { to: "/app/pagos-proveedores", label: "Pagos a proveedores", icon: Send, tid: "nav-pagos-proveedores", module: "tesoreria" },
  { to: "/app/caja-chica", label: "Caja chica", icon: PiggyBank, tid: "nav-caja-chica", module: "tesoreria" },
];

const ADMIN_NAV = [
  { to: "/app/empresas", label: "Empresas", icon: Building2, tid: "nav-empresas", module: "empresas" },
  { to: "/app/usuarios", label: "Usuarios", icon: Users, tid: "nav-usuarios", module: "usuarios" },
];

function filterNav(items, canRead) {
  return items.filter((i) => canRead(i.module));
}

function canManageOrganization(user) {
  if (!user || user.solo_lectura) return false;
  if (user.role === "superadmin") return true;
  return ["owner", "admin"].includes(user.role);
}

const NAV_SECTIONS_STORAGE_KEY = "aequor.nav.sections";

function loadOpenSections(defaults) {
  try {
    const raw = localStorage.getItem(NAV_SECTIONS_STORAGE_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw);
    return { ...defaults, ...parsed };
  } catch {
    return defaults;
  }
}

function NavSection({ id, label, items, open, onToggle, onNav, pathname }) {
  if (!items.length) return null;
  const hasActive = items.some((i) => pathname === i.to || pathname.startsWith(`${i.to}/`));

  return (
    <div className="pt-2 first:pt-0" data-testid={`nav-section-${id}`}>
      <button
        type="button"
        onClick={() => onToggle(id)}
        data-testid={`nav-section-toggle-${id}`}
        aria-expanded={open}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-sm text-[10px] tracking-[0.2em] uppercase font-semibold transition-colors ${
          hasActive
            ? "text-foreground"
            : "text-muted-foreground/70 hover:text-foreground hover:bg-muted/60"
        }`}
      >
        <span>{label}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${open ? "rotate-0" : "-rotate-90"}`}
          strokeWidth={2}
        />
      </button>
      {open && (
        <div className="space-y-0.5 pb-1">
          {items.map((item) => (
            <NavItem key={item.to} {...item} onNav={onNav} />
          ))}
        </div>
      )}
    </div>
  );
}

function SidebarContent({ user, onNav }) {
  const { canRead } = usePermissions();
  const location = useLocation();
  const pathname = location.pathname;

  const contabilidad = filterNav(NAV, canRead);
  const reportes = filterNav(REPORTES_NAV, canRead);
  const comercial = filterNav(COMERCIAL_NAV, canRead);
  const remuneraciones = filterNav(REMUNERACIONES_NAV, canRead);
  const tesoreria = filterNav(TESORERIA_NAV, canRead);
  const adminNav = [
    ...(canManageOrganization(user) ? filterNav(ADMIN_NAV, canRead) : []),
    ...(user?.role === "superadmin" && !user?.organizacion_id
      ? [{ to: "/app/agenda", label: "Agenda de asesorías", icon: CalendarClock, tid: "nav-agenda" }] : []),
  ];

  const sections = [
    { id: "contabilidad", label: "Contabilidad", items: contabilidad },
    { id: "reportes", label: "Reportes", items: reportes },
    { id: "comercial", label: "Comercial", items: comercial },
    { id: "remuneraciones", label: "Remuneraciones", items: remuneraciones },
    { id: "tesoreria", label: "Tesorería", items: tesoreria },
    { id: "admin", label: "Administración", items: adminNav },
  ].filter((s) => s.items.length > 0);

  const defaults = Object.fromEntries(sections.map((s) => [s.id, false]));
  const [openSections, setOpenSections] = useState(() => loadOpenSections(defaults));

  // Abre automáticamente la categoría de la ruta actual
  useEffect(() => {
    const active = sections.find((s) =>
      s.items.some((i) => pathname === i.to || pathname.startsWith(`${i.to}/`))
    );
    if (!active) return;
    setOpenSections((prev) => {
      if (prev[active.id]) return prev;
      const next = { ...prev, [active.id]: true };
      try { localStorage.setItem(NAV_SECTIONS_STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const toggleSection = (id) => {
    setOpenSections((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem(NAV_SECTIONS_STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  return (
    <>
      <div className="px-5 py-5 border-b border-sidebar-border">
        <div className="flex items-center gap-3">
          <BrandLogo
            className="h-12 w-12 !max-w-none object-cover object-[center_12%]"
            testid="sidebar-logo"
          />
          <div className="min-w-0">
            <div className="font-display text-[17px] leading-tight tracking-[0.12em] uppercase text-foreground">
              Aequor
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="h-px w-3 bg-gold" />
              <span className="text-[9px] tracking-[0.28em] uppercase text-gold font-semibold">
                Group
              </span>
              <span className="h-px w-3 bg-gold" />
            </div>
            <div className="text-[10px] tracking-[0.12em] uppercase text-muted-foreground/80 truncate max-w-[130px] mt-1">
              {user?.organizacion?.nombre || "ERP Contable"}
            </div>
            {user?.organizacion_id && !user?.solo_lectura && (
              <SubscriptionNotice org={user.organizacion} compact />
            )}
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto" data-testid="sidebar-nav">
        {sections.map((s) => (
          <NavSection
            key={s.id}
            id={s.id}
            label={s.label}
            items={s.items}
            open={!!openSections[s.id]}
            onToggle={toggleSection}
            onNav={onNav}
            pathname={pathname}
          />
        ))}
      </nav>
    </>
  );
}

export default function AppLayout() {
  const { user, logout, setEmpresaActiva, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [empresas, setEmpresas] = useState([]);
  const [sheetOpen, setSheetOpen] = useState(false);

  const loadEmpresas = async () => {
    try {
      const { data } = await api.get("/empresas");
      setEmpresas(data);
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  useEffect(() => {
    loadEmpresas();
  }, []);

  // Close mobile sheet on route change
  useEffect(() => { setSheetOpen(false); }, [location.pathname]);

  const empresaActiva =
    empresas.find((e) => e.id === user?.empresa_activa_id) || empresas[0];

  const handleSwitchEmpresa = async (eid) => {
    if (eid === user?.empresa_activa_id) return;
    try {
      toast.success("Cambiando empresa…");
      await setEmpresaActiva(eid);
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="app-layout">
      {/* Desktop Sidebar (md+) */}
      <aside className="hidden md:flex w-60 shrink-0 border-r border-sidebar-border bg-sidebar flex-col">
        <SidebarContent user={user} />

        <div className="border-t border-sidebar-border p-3">
          <div className="px-2 py-1.5">
            <div className="text-[13px] font-medium text-foreground truncate">{user?.name}</div>
            <div className="text-[11px] text-muted-foreground">
              {user?.role_label || user?.role}
            </div>
            {user?.organizacion_id && !user?.solo_lectura && (
              <SubscriptionNotice org={user.organizacion} compact />
            )}
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full mt-1 flex items-center gap-2 px-2 py-1.5 rounded-sm text-[13px] text-muted-foreground hover:bg-muted hover:text-destructive transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" strokeWidth={1.75} /> Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar Drawer */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="left" className="w-72 p-0 bg-sidebar flex flex-col" data-testid="mobile-sidebar">
          <SheetTitle className="sr-only">Menú de navegación</SheetTitle>
          <SheetDescription className="sr-only">Navega entre los módulos de Aequor.</SheetDescription>
          <SidebarContent user={user} onNav={() => setSheetOpen(false)} />
          <div className="border-t border-sidebar-border p-3">
            <div className="px-2 py-1.5">
              <div className="text-[13px] font-medium text-foreground truncate">{user?.name}</div>
              <div className="text-[11px] text-muted-foreground">
                {user?.role_label || user?.role}
              </div>
              {user?.organizacion_id && !user?.solo_lectura && (
                <SubscriptionNotice org={user.organizacion} compact />
              )}
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button-mobile"
              className="w-full mt-1 flex items-center gap-2 px-2 py-1.5 rounded-sm text-[13px] text-muted-foreground hover:bg-muted hover:text-destructive transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" strokeWidth={1.75} /> Cerrar sesión
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-border bg-card px-3 sm:px-6 flex items-center justify-between gap-2 shadow-[inset_0_-1px_0_0_hsl(var(--gold)/0.35)]">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {/* Hamburger (mobile only) */}
            <button
              onClick={() => setSheetOpen(true)}
              data-testid="mobile-menu-button"
              className="md:hidden p-2 -ml-1 rounded-sm hover:bg-muted text-foreground"
              aria-label="Abrir menú"
            >
              <Menu className="h-5 w-5" strokeWidth={1.75} />
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="empresa-selector"
                  className="flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded-sm hover:bg-muted text-left min-w-0"
                >
                  <Building2 className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.75} />
                  <div className="min-w-0">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground/70 leading-none hidden sm:block">
                      Empresa activa
                    </div>
                    <div className="text-[13px] font-medium text-foreground leading-tight truncate max-w-[160px] sm:max-w-none">
                      {empresaActiva?.nombre || "—"}
                    </div>
                  </div>
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground ml-1 shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-72">
                <DropdownMenuLabel>Cambiar empresa</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {empresas.map((e) => (
                  <DropdownMenuItem
                    key={e.id}
                    data-testid={`empresa-option-${e.id}`}
                    onClick={() => handleSwitchEmpresa(e.id)}
                    className="flex justify-between items-center"
                  >
                    <div>
                      <div className="text-[13px] font-medium">{e.nombre}</div>
                      <div className="text-[11px] text-muted-foreground font-mono">{e.rut}</div>
                    </div>
                    {empresaActiva?.id === e.id && (
                      <Check className="h-3.5 w-3.5 text-primary" />
                    )}
                  </DropdownMenuItem>
                ))}
                {empresas.length === 0 && (
                  <div className="px-2 py-3 text-[12px] text-muted-foreground">
                    No tienes empresas. Crea una en Administración → Empresas.
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <ThemeToggle />
            <span className="hidden lg:inline text-[11px] tracking-[0.2em] uppercase text-muted-foreground/70">
              {new Date().toLocaleDateString("es-CL", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
            <span className="lg:hidden text-[11px] tracking-[0.15em] uppercase text-muted-foreground/70">
              {new Date().toLocaleDateString("es-CL", { day: "2-digit", month: "short" })}
            </span>
          </div>
        </header>

        <DemoBanner />
        <SubscriptionBanner />
        <OrganizadorReminders />
        {/* pb-20: margen para widgets flotantes externos (esquina inferior) sobre tablas y paginación */}
        <main className="flex-1 overflow-auto pb-20" data-testid="app-main">
          {/* key fuerza remount al cambiar de empresa: evita datos residuales de otro tenant */}
          <Outlet
            key={user?.empresa_activa_id || "sin-empresa"}
            context={{ refreshEmpresas: loadEmpresas, refreshUser: refresh, empresaActiva }}
          />
        </main>
      </div>
    </div>
  );
}

function NavItem({ to, label, icon: Icon, tid, onNav }) {
  return (
    <NavLink
      to={to}
      data-testid={tid}
      onClick={onNav}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-3 py-2 rounded-sm text-[13px] transition-colors ${
          isActive
            ? "bg-primary text-primary-foreground"
            : "text-muted-foreground hover:bg-muted hover:text-foreground"
        }`
      }
    >
      <Icon className="h-4 w-4" strokeWidth={1.75} />
      {label}
    </NavLink>
  );
}
